import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { ShoppingBag, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const RAZORPAY_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT_URL;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function BuyBookDialog({ book, triggerClass = "", triggerLabel, size = "md", testIdPrefix = "buy" }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", address: "", city: "", state: "", pincode: "", quantity: 1, notes: "" });
  const [payMethod, setPayMethod] = useState("razorpay"); // "razorpay" | "cod"

  useEffect(() => {
    if (open) loadRazorpayScript();
  }, [open]);

  const total = (Number(book.price) || 0) * (Number(form.quantity) || 1);
  const [shipping, setShipping] = useState({ fee: 0, loading: false, info: null });

  useEffect(() => {
    const pin = form.pincode.trim();
    if (!open || pin.length !== 6) {
      setShipping({ fee: 0, loading: false, info: null });
      return;
    }
    setShipping((s) => ({ ...s, loading: true, fee: 0 }));
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/shipping/calculate", { pincode: pin, quantity: Number(form.quantity) || 1 });
        if (!cancelled) setShipping({ fee: Number(data.shipping_fee_inr) || 0, loading: false, info: data });
      } catch {
        if (!cancelled) setShipping({ fee: 70, loading: false, info: null });
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.pincode, form.quantity, open]);

  const grandTotal = total + (form.pincode.trim().length === 6 ? shipping.fee : 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please fill name and phone");
      return;
    }
    if (payMethod === "cod" && form.pincode.trim().length !== 6) {
      toast.error("Enter a valid 6-digit pincode for Cash on Delivery");
      return;
    }
    setSubmitting(true);

    if (payMethod === "cod") {
      try {
        const { data } = await api.post("/orders/create", {
          book_id: book.id,
          quantity: Number(form.quantity),
          name: form.name.trim(),
          phone: form.phone.trim(),
          address: form.address.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          pincode: form.pincode.trim(),
          notes: form.notes.trim(),
          payment_method: "cod",
        });
        toast.success(`COD order placed. Pay ₹${data.amount_inr} to the courier on delivery.`);
        setOpen(false);
        setForm({ name: "", phone: "", address: "", city: "", state: "", pincode: "", quantity: 1, notes: "" });
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not place COD order");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    const scriptOk = await loadRazorpayScript();
    if (!scriptOk || !window.Razorpay) {
      toast.error("Could not load Razorpay Checkout. Check your internet connection.");
      setSubmitting(false);
      return;
    }

    let orderRes;
    try {
      const { data } = await api.post("/orders/create", {
        book_id: book.id,
        quantity: Number(form.quantity),
        name: form.name.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        pincode: form.pincode.trim(),
        notes: form.notes.trim(),
        payment_method: "razorpay",
      });
      orderRes = data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create order");
      setSubmitting(false);
      return;
    }

    const options = {
      key: orderRes.razorpay_key_id,
      amount: orderRes.amount_paise,
      currency: orderRes.currency,
      name: "Chand Book Depot",
      description: `${orderRes.book_title} × ${form.quantity}`,
      order_id: orderRes.razorpay_order_id,
      prefill: {
        name: form.name.trim(),
        contact: form.phone.trim(),
      },
      notes: {
        book_id: book.id,
        internal_order_id: orderRes.order_id,
      },
      theme: { color: "#E05A47" },
      handler: async (rzpRes) => {
        try {
          await api.post("/orders/verify", {
            order_id: orderRes.order_id,
            razorpay_order_id: rzpRes.razorpay_order_id,
            razorpay_payment_id: rzpRes.razorpay_payment_id,
            razorpay_signature: rzpRes.razorpay_signature,
          });
          toast.success("Payment received. We'll contact you shortly.");
          setOpen(false);
          setForm({ name: "", phone: "", address: "", city: "", state: "", pincode: "", quantity: 1, notes: "" });
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail) || "Payment verification failed");
        }
      },
      modal: {
        ondismiss: () => setSubmitting(false),
      },
    };

    try {
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed");
        setSubmitting(false);
      });
      rzp.open();
    } catch (err) {
      toast.error("Could not open Razorpay checkout");
      setSubmitting(false);
    }
  };

  const btnSize = size === "sm" ? "h-9 text-xs px-4" : "h-10 text-sm px-5";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button
        data-testid={`${testIdPrefix}-book-${book.id}`}
        onClick={() => setOpen(true)}
        className={`inline-flex items-center justify-center gap-2 rounded-full bg-[#0F172A] hover:bg-[#E05A47] text-white transition-colors ${btnSize} ${triggerClass}`}
        style={{ transitionProperty: "background-color" }}
      >
        <ShoppingBag size={size === "sm" ? 12 : 14}/>
        {triggerLabel || `Buy · ₹${Number(book.price).toFixed(0)}`}
      </button>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl leading-tight">Buy this book</DialogTitle>
          <DialogDescription className="text-xs">
            {book.title} · <span className="text-[#E05A47] font-medium">₹{Number(book.price).toFixed(0)}</span> per copy
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} data-testid={`buy-form-${book.id}`} className="space-y-4">
          <Field label="Your name">
            <Input data-testid="buy-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </Field>
          <Field label="Phone (WhatsApp preferred)">
            <Input data-testid="buy-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="10-digit mobile" required />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Quantity">
              <Input data-testid="buy-quantity-input" type="number" min="1" max="20" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value || 1) })}/>
            </Field>
            <Field label="Total">
              <div className="h-10 flex items-center px-3 rounded-md border border-neutral-200 bg-neutral-50 text-[#E05A47] font-medium">
                ₹ {total.toFixed(0)}
              </div>
            </Field>
          </div>
          <Field label="Delivery address (leave blank for shop pickup)">
            <Textarea data-testid="buy-address-input" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="House / Street / Area"/>
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Pincode">
              <Input data-testid="buy-pincode-input" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/[^0-9]/g, "").slice(0,6) })} placeholder="110085"/>
            </Field>
            <Field label="City">
              <Input data-testid="buy-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Delhi"/>
            </Field>
            <Field label="State">
              <Input data-testid="buy-state-input" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="Delhi"/>
            </Field>
          </div>
          <p className="text-[11px] text-neutral-500 -mt-1">Fill pincode for home delivery via Shiprocket. Leave blank for pickup at shop.</p>
          <Field label="Notes (optional)">
            <Input data-testid="buy-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any message for the shop"/>
          </Field>

          <div data-testid="buy-order-summary" className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm space-y-1">
            <div className="flex justify-between text-neutral-600">
              <span>Books</span>
              <span>₹ {total.toFixed(0)}</span>
            </div>
            <div className="flex justify-between text-neutral-600">
              <span data-testid="buy-shipping-label">Shipping{shipping.info?.courier_name ? ` · ${shipping.info.courier_name}` : ""}</span>
              <span data-testid="buy-shipping-fee">
                {form.pincode.trim().length !== 6
                  ? "Free (shop pickup)"
                  : shipping.loading
                    ? "Calculating…"
                    : `₹ ${shipping.fee.toFixed(0)}`}
              </span>
            </div>
            {form.pincode.trim().length === 6 && !shipping.loading && shipping.info?.source === "fallback" && (
              <div className="text-[11px] text-neutral-400">Standard flat rate applied</div>
            )}
            <div className="flex justify-between border-t border-neutral-200 pt-1 font-medium text-[#0F172A]">
              <span>Total</span>
              <span data-testid="buy-grand-total" className="text-[#E05A47]">₹ {grandTotal.toFixed(0)}</span>
            </div>
          </div>

          <div data-testid="buy-payment-method" className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">Payment method</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="buy-pay-razorpay"
                onClick={() => setPayMethod("razorpay")}
                className={`h-10 rounded-full text-xs font-medium border transition-colors ${payMethod === "razorpay" ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-neutral-700 border-neutral-200 hover:border-[#0F172A]"}`}
                style={{ transitionProperty: "background-color, color, border-color" }}
              >
                Pay online
              </button>
              <button
                type="button"
                data-testid="buy-pay-cod"
                onClick={() => setPayMethod("cod")}
                className={`h-10 rounded-full text-xs font-medium border transition-colors ${payMethod === "cod" ? "bg-[#4A6741] text-white border-[#4A6741]" : "bg-white text-neutral-700 border-neutral-200 hover:border-[#4A6741]"}`}
                style={{ transitionProperty: "background-color, color, border-color" }}
              >
                Cash on Delivery
              </button>
            </div>
            {payMethod === "cod" && (
              <p className="text-[11px] text-neutral-500">Requires a valid 6-digit delivery pincode. Pay the courier in cash on arrival.</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="buy-cancel-btn">Cancel</Button>
            <Button
              type="submit"
              data-testid="buy-pay-btn"
              disabled={submitting}
              className={`text-white transition-colors ${payMethod === "cod" ? "bg-[#4A6741] hover:bg-[#3a5233]" : "bg-[#E05A47] hover:bg-[#c94a38]"}`}
              style={{ transitionProperty: "background-color" }}
            >
              {submitting
                ? <><Loader2 size={14} className="mr-2 animate-spin"/>{payMethod === "cod" ? "Placing…" : "Opening…"}</>
                : payMethod === "cod"
                  ? `Place COD order · ₹${grandTotal.toFixed(0)}`
                  : `Pay ₹${grandTotal.toFixed(0)}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
